import { ApiService } from "@core/api/api-service";
import { BlocksContent } from "@strapi/blocks-react-renderer";

export enum MediaType {
	ARTICLE = "ARTICLE",
	VIDEO = "VIDEO",
}

export interface ContentNode {
	type: string;
	text?: string;
	children?: ContentNode[];
	bold?: boolean;
	italic?: boolean;
	underline?: boolean;
	level?: number;
	ordered?: boolean;
	url?: string;
}

export interface ImageFormat {
	ext: string;
	url: string;
	hash: string;
	mime: string;
	name: string;
	path: string | null;
	size: number;
	width: number;
	height: number;
	sizeInBytes: number;
	provider_metadata: {
		public_id: string;
		resource_type: string;
	};
}

export interface ImageData {
	id: number;
	ext: string;
	url: string;
	hash: string;
	mime: string;
	name: string;
	size: number;
	width: number;
	height: number;
	caption: string | null;
	formats: {
		small?: ImageFormat;
		thumbnail?: ImageFormat;
		medium?: ImageFormat;
		large?: ImageFormat;
	};
	provider: string;
	createdAt: string;
	updatedAt: string;
	documentId: string;
	previewUrl: string | null;
	publishedAt: string | null;
	alternativeText: string | null;
	locale?: string | null;
	provider_metadata: {
		public_id: string;
		resource_type: string;
	};
}

export interface EducationalArticleTipsModule {
	id: number;
	__component: "embed.educational-article-tips-module";
	image: ImageData;
	title: string;
	content: BlocksContent;
}

export interface QuoteAuthor {
	id: number;
	documentId: string;
	name: string;
	title: string;
	image?: ImageData;
	locale?: string;
	createdAt?: string;
	updatedAt?: string;
	publishedAt?: string | null;
}

export interface QuoteModule {
	id: number;
	__component: "embed.quote-module";
	quote: string;
	author?: QuoteAuthor;
}

export type ArticleModule = EducationalArticleTipsModule | QuoteModule;

export interface LearnItem {
	documentId: string;
	title: string;
	mediaType: MediaType;
	category?: string;
	author?: string;
	publicationDate?: string;
	expirationDate?: string;
	tags?: string[];
	mediaUrl?: string;
	previewUrl?: string | null;
	duration?: number | null;
	content?: BlocksContent[];
	callout?: BlocksContent[];
	reference?: string;
	footer?: string;
	articleModules?: ArticleModule[];
}

export interface LearnListResponse {
	items: LearnItem[];
	page: number;
	pageSize: number;
	totalItems: number;
	totalPages: number;
}

export class LearnService {
	private readonly rootPath = "/learn";

	constructor(private readonly apiService: ApiService) {}

	async ping(): Promise<void> {
		await this.apiService.get(this.rootPath);
	}

	async getItems(page = 0, pageSize = 20, mediaType?: MediaType): Promise<LearnListResponse> {
		const params: Record<string, unknown> = { page, pageSize };
		if (mediaType) params.mediaType = mediaType;
		const result = await this.apiService.get<LearnListResponse>(this.rootPath, { params });
		return result.data;
	}

	async getItem(documentId: string): Promise<LearnItem> {
		const result = await this.apiService.get<LearnItem>(`${this.rootPath}/${documentId}`);
		return result.data;
	}
}
